import { V1ClockInfo, V1ClockTime } from '../../api/v1Mqtt.api';

/**
 * A V1 / Coldfire tub's clock. The tub has no time zone: it keeps a bare wall-clock time
 * (a Coldfire its local "spa time", a Yoctub UTC, as classic sets them), answered on the
 * classic web broker as `{year, month, day, hour, minute, second}`.
 */

/**
 * Classic's time zone list (its .NET TimeZoneInfo zones, once; classic's page repeated
 * them), each on the IANA zone the browser can compute, plus Alberta: it stays on
 * UTC−6 all year now, so a fixed offset rather than Windows' Mountain Time. Zones the
 * browser does not know are dropped.
 */
const CLASSIC_ZONES: { label: string; timeZone: string }[] = [
    { label: '(UTC-12:00) International Date Line West', timeZone: 'Etc/GMT+12' },
    { label: '(UTC-11:00) Coordinated Universal Time-11', timeZone: 'Etc/GMT+11' },
    { label: '(UTC-10:00) Aleutian Islands', timeZone: 'America/Adak' },
    { label: '(UTC-10:00) Hawaii', timeZone: 'Pacific/Honolulu' },
    { label: '(UTC-09:30) Marquesas Islands', timeZone: 'Pacific/Marquesas' },
    { label: '(UTC-09:00) Alaska', timeZone: 'America/Anchorage' },
    { label: '(UTC-09:00) Coordinated Universal Time-09', timeZone: 'Etc/GMT+9' },
    { label: '(UTC-08:00) Baja California', timeZone: 'America/Tijuana' },
    { label: '(UTC-08:00) Coordinated Universal Time-08', timeZone: 'Etc/GMT+8' },
    { label: '(UTC-08:00) Pacific Time (US & Canada)', timeZone: 'America/Vancouver' },
    { label: '(UTC-07:00) Arizona', timeZone: 'America/Phoenix' },
    { label: '(UTC-07:00) Chihuahua, La Paz, Mazatlan', timeZone: 'America/Mazatlan' },
    { label: '(UTC-07:00) Mountain Time (US & Canada)', timeZone: 'America/Denver' },
    { label: '(UTC-07:00) Yukon', timeZone: 'America/Whitehorse' },
    { label: '(UTC-06:00) Alberta', timeZone: 'Etc/GMT+6' },
    { label: '(UTC-06:00) Central America', timeZone: 'America/Guatemala' },
    { label: '(UTC-06:00) Central Time (US & Canada)', timeZone: 'America/Winnipeg' },
    { label: '(UTC-06:00) Easter Island', timeZone: 'Pacific/Easter' },
    { label: '(UTC-06:00) Guadalajara, Mexico City, Monterrey', timeZone: 'America/Mexico_City' },
    { label: '(UTC-06:00) Saskatchewan', timeZone: 'America/Regina' },
    { label: '(UTC-05:00) Bogota, Lima, Quito, Rio Branco', timeZone: 'America/Bogota' },
    { label: '(UTC-05:00) Chetumal', timeZone: 'America/Cancun' },
    { label: '(UTC-05:00) Eastern Time (US & Canada)', timeZone: 'America/Toronto' },
    { label: '(UTC-05:00) Haiti', timeZone: 'America/Port-au-Prince' },
    { label: '(UTC-05:00) Havana', timeZone: 'America/Havana' },
    { label: '(UTC-05:00) Indiana (East)', timeZone: 'America/Indiana/Indianapolis' },
    { label: '(UTC-05:00) Turks and Caicos', timeZone: 'America/Grand_Turk' },
    { label: '(UTC-04:00) Asuncion', timeZone: 'America/Asuncion' },
    { label: '(UTC-04:00) Atlantic Time (Canada)', timeZone: 'America/Halifax' },
    { label: '(UTC-04:00) Caracas', timeZone: 'America/Caracas' },
    { label: '(UTC-04:00) Cuiaba', timeZone: 'America/Cuiaba' },
    { label: '(UTC-04:00) Georgetown, La Paz, Manaus, San Juan', timeZone: 'America/La_Paz' },
    { label: '(UTC-04:00) Santiago', timeZone: 'America/Santiago' },
    { label: '(UTC-03:30) Newfoundland', timeZone: 'America/St_Johns' },
    { label: '(UTC-03:00) Araguaina', timeZone: 'America/Araguaina' },
    { label: '(UTC-03:00) Brasilia', timeZone: 'America/Sao_Paulo' },
    { label: '(UTC-03:00) Cayenne, Fortaleza', timeZone: 'America/Cayenne' },
    { label: '(UTC-03:00) City of Buenos Aires', timeZone: 'America/Argentina/Buenos_Aires' },
    { label: '(UTC-03:00) Greenland', timeZone: 'America/Nuuk' },
    { label: '(UTC-03:00) Montevideo', timeZone: 'America/Montevideo' },
    { label: '(UTC-03:00) Punta Arenas', timeZone: 'America/Punta_Arenas' },
    { label: '(UTC-03:00) Saint Pierre and Miquelon', timeZone: 'America/Miquelon' },
    { label: '(UTC-03:00) Salvador', timeZone: 'America/Bahia' },
    { label: '(UTC-02:00) Coordinated Universal Time-02', timeZone: 'Etc/GMT+2' },
    { label: '(UTC-02:00) Mid-Atlantic - Old', timeZone: 'Etc/GMT+2' },
    { label: '(UTC-01:00) Azores', timeZone: 'Atlantic/Azores' },
    { label: '(UTC-01:00) Cabo Verde Is.', timeZone: 'Atlantic/Cape_Verde' },
    { label: '(UTC) Coordinated Universal Time', timeZone: 'UTC' },
    { label: '(UTC+00:00) Dublin, Edinburgh, Lisbon, London', timeZone: 'Europe/London' },
    { label: '(UTC+00:00) Monrovia, Reykjavik', timeZone: 'Atlantic/Reykjavik' },
    { label: '(UTC+00:00) Sao Tome', timeZone: 'Africa/Sao_Tome' },
    { label: '(UTC+01:00) Casablanca', timeZone: 'Africa/Casablanca' },
    { label: '(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna', timeZone: 'Europe/Berlin' },
    { label: '(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague', timeZone: 'Europe/Budapest' },
    { label: '(UTC+01:00) Brussels, Copenhagen, Madrid, Paris', timeZone: 'Europe/Paris' },
    { label: '(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb', timeZone: 'Europe/Warsaw' },
    { label: '(UTC+01:00) West Central Africa', timeZone: 'Africa/Lagos' },
    { label: '(UTC+02:00) Amman', timeZone: 'Asia/Amman' },
    { label: '(UTC+02:00) Athens, Bucharest', timeZone: 'Europe/Bucharest' },
    { label: '(UTC+02:00) Beirut', timeZone: 'Asia/Beirut' },
    { label: '(UTC+02:00) Cairo', timeZone: 'Africa/Cairo' },
    { label: '(UTC+02:00) Chisinau', timeZone: 'Europe/Chisinau' },
    { label: '(UTC+02:00) Damascus', timeZone: 'Asia/Damascus' },
    { label: '(UTC+02:00) Gaza, Hebron', timeZone: 'Asia/Hebron' },
    { label: '(UTC+02:00) Harare, Pretoria', timeZone: 'Africa/Johannesburg' },
    { label: '(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius', timeZone: 'Europe/Helsinki' },
    { label: '(UTC+02:00) Jerusalem', timeZone: 'Asia/Jerusalem' },
    { label: '(UTC+02:00) Juba', timeZone: 'Africa/Juba' },
    { label: '(UTC+02:00) Kaliningrad', timeZone: 'Europe/Kaliningrad' },
    { label: '(UTC+02:00) Khartoum', timeZone: 'Africa/Khartoum' },
    { label: '(UTC+02:00) Tripoli', timeZone: 'Africa/Tripoli' },
    { label: '(UTC+02:00) Windhoek', timeZone: 'Africa/Windhoek' },
    { label: '(UTC+03:00) Baghdad', timeZone: 'Asia/Baghdad' },
    { label: '(UTC+03:00) Istanbul', timeZone: 'Europe/Istanbul' },
    { label: '(UTC+03:00) Kuwait, Riyadh', timeZone: 'Asia/Riyadh' },
    { label: '(UTC+03:00) Minsk', timeZone: 'Europe/Minsk' },
    { label: '(UTC+03:00) Moscow, St. Petersburg', timeZone: 'Europe/Moscow' },
    { label: '(UTC+03:00) Nairobi', timeZone: 'Africa/Nairobi' },
    { label: '(UTC+03:00) Volgograd', timeZone: 'Europe/Volgograd' },
    { label: '(UTC+03:30) Tehran', timeZone: 'Asia/Tehran' },
    { label: '(UTC+04:00) Abu Dhabi, Muscat', timeZone: 'Asia/Dubai' },
    { label: '(UTC+04:00) Astrakhan, Ulyanovsk', timeZone: 'Europe/Astrakhan' },
    { label: '(UTC+04:00) Baku', timeZone: 'Asia/Baku' },
    { label: '(UTC+04:00) Izhevsk, Samara', timeZone: 'Europe/Samara' },
    { label: '(UTC+04:00) Port Louis', timeZone: 'Indian/Mauritius' },
    { label: '(UTC+04:00) Saratov', timeZone: 'Europe/Saratov' },
    { label: '(UTC+04:00) Tbilisi', timeZone: 'Asia/Tbilisi' },
    { label: '(UTC+04:00) Yerevan', timeZone: 'Asia/Yerevan' },
    { label: '(UTC+04:30) Kabul', timeZone: 'Asia/Kabul' },
    { label: '(UTC+05:00) Ashgabat, Tashkent', timeZone: 'Asia/Tashkent' },
    { label: '(UTC+05:00) Ekaterinburg', timeZone: 'Asia/Yekaterinburg' },
    { label: '(UTC+05:00) Islamabad, Karachi', timeZone: 'Asia/Karachi' },
    { label: '(UTC+05:00) Qyzylorda', timeZone: 'Asia/Qyzylorda' },
    { label: '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi', timeZone: 'Asia/Kolkata' },
    { label: '(UTC+05:30) Sri Jayawardenepura', timeZone: 'Asia/Colombo' },
    { label: '(UTC+05:45) Kathmandu', timeZone: 'Asia/Kathmandu' },
    { label: '(UTC+06:00) Astana', timeZone: 'Asia/Almaty' },
    { label: '(UTC+06:00) Dhaka', timeZone: 'Asia/Dhaka' },
    { label: '(UTC+06:00) Omsk', timeZone: 'Asia/Omsk' },
    { label: '(UTC+06:30) Yangon (Rangoon)', timeZone: 'Asia/Yangon' },
    { label: '(UTC+07:00) Bangkok, Hanoi, Jakarta', timeZone: 'Asia/Bangkok' },
    { label: '(UTC+07:00) Barnaul, Gorno-Altaysk', timeZone: 'Asia/Barnaul' },
    { label: '(UTC+07:00) Hovd', timeZone: 'Asia/Hovd' },
    { label: '(UTC+07:00) Krasnoyarsk', timeZone: 'Asia/Krasnoyarsk' },
    { label: '(UTC+07:00) Novosibirsk', timeZone: 'Asia/Novosibirsk' },
    { label: '(UTC+07:00) Tomsk', timeZone: 'Asia/Tomsk' },
    { label: '(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi', timeZone: 'Asia/Shanghai' },
    { label: '(UTC+08:00) Irkutsk', timeZone: 'Asia/Irkutsk' },
    { label: '(UTC+08:00) Kuala Lumpur, Singapore', timeZone: 'Asia/Singapore' },
    { label: '(UTC+08:00) Perth', timeZone: 'Australia/Perth' },
    { label: '(UTC+08:00) Taipei', timeZone: 'Asia/Taipei' },
    { label: '(UTC+08:00) Ulaanbaatar', timeZone: 'Asia/Ulaanbaatar' },
    { label: '(UTC+08:45) Eucla', timeZone: 'Australia/Eucla' },
    { label: '(UTC+09:00) Chita', timeZone: 'Asia/Chita' },
    { label: '(UTC+09:00) Osaka, Sapporo, Tokyo', timeZone: 'Asia/Tokyo' },
    { label: '(UTC+09:00) Pyongyang', timeZone: 'Asia/Pyongyang' },
    { label: '(UTC+09:00) Seoul', timeZone: 'Asia/Seoul' },
    { label: '(UTC+09:00) Yakutsk', timeZone: 'Asia/Yakutsk' },
    { label: '(UTC+09:30) Adelaide', timeZone: 'Australia/Adelaide' },
    { label: '(UTC+09:30) Darwin', timeZone: 'Australia/Darwin' },
    { label: '(UTC+10:00) Brisbane', timeZone: 'Australia/Brisbane' },
    { label: '(UTC+10:00) Canberra, Melbourne, Sydney', timeZone: 'Australia/Sydney' },
    { label: '(UTC+10:00) Guam, Port Moresby', timeZone: 'Pacific/Port_Moresby' },
    { label: '(UTC+10:00) Hobart', timeZone: 'Australia/Hobart' },
    { label: '(UTC+10:00) Vladivostok', timeZone: 'Asia/Vladivostok' },
    { label: '(UTC+10:30) Lord Howe Island', timeZone: 'Australia/Lord_Howe' },
    { label: '(UTC+11:00) Bougainville Island', timeZone: 'Pacific/Bougainville' },
    { label: '(UTC+11:00) Chokurdakh', timeZone: 'Asia/Srednekolymsk' },
    { label: '(UTC+11:00) Magadan', timeZone: 'Asia/Magadan' },
    { label: '(UTC+11:00) Norfolk Island', timeZone: 'Pacific/Norfolk' },
    { label: '(UTC+11:00) Sakhalin', timeZone: 'Asia/Sakhalin' },
    { label: '(UTC+11:00) Solomon Is., New Caledonia', timeZone: 'Pacific/Guadalcanal' },
    { label: '(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky', timeZone: 'Asia/Kamchatka' },
    { label: '(UTC+12:00) Auckland, Wellington', timeZone: 'Pacific/Auckland' },
    { label: '(UTC+12:00) Coordinated Universal Time+12', timeZone: 'Etc/GMT-12' },
    { label: '(UTC+12:00) Fiji', timeZone: 'Pacific/Fiji' },
    { label: '(UTC+12:00) Petropavlovsk-Kamchatsky - Old', timeZone: 'Asia/Kamchatka' },
    { label: '(UTC+12:45) Chatham Islands', timeZone: 'Pacific/Chatham' },
    { label: '(UTC+13:00) Coordinated Universal Time+13', timeZone: 'Etc/GMT-13' },
    { label: "(UTC+13:00) Nuku'alofa", timeZone: 'Pacific/Tongatapu' },
    { label: '(UTC+13:00) Samoa', timeZone: 'Pacific/Apia' },
    { label: '(UTC+14:00) Kiritimati Island', timeZone: 'Pacific/Kiritimati' },
];

const isKnownZone = (timeZone: string): boolean => {
    try { new Intl.DateTimeFormat('en-US', { timeZone }); return true; } catch { return false; }
};

export const CLOCK_ZONES = CLASSIC_ZONES.filter((z) => isKnownZone(z.timeZone));

export const DEFAULT_CLOCK_ZONE = '(UTC-06:00) Alberta';

const isInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

/**
 * The tub's answer as a full-year, 1-based-month time, or null when it is not a clock.
 * Year arrives full (2026) from a Coldfire; a two-digit one is taken as 20xx.
 */
export const parseTubClock = (
    payload: any,
    info: Pick<V1ClockInfo, 'month_is_zero_based'>,
): V1ClockTime | null => {
    if (!payload || typeof payload !== 'object') return null;
    const { year, month, day, hour, minute, second } = payload;
    if (![year, month, day, hour, minute, second].every(isInt)) return null;

    const time: V1ClockTime = {
        year: year < 100 ? year + 2000 : year,
        month: info.month_is_zero_based ? month + 1 : month,
        day,
        hour,
        minute,
        second,
    };

    return time.month >= 1 && time.month <= 12 && time.day >= 1 && time.day <= 31
        && time.hour <= 23 && time.minute <= 59 && time.second <= 59 ? time : null;
};

/** A wall-clock time as epoch ms, read as if it were UTC: lets it be advanced and formatted. */
export const clockToMs = (t: V1ClockTime): number =>
    Date.UTC(t.year, t.month - 1, t.day, t.hour, t.minute, t.second);

export const msToClock = (ms: number): V1ClockTime => {
    const d = new Date(ms);
    return {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
        hour: d.getUTCHours(),
        minute: d.getUTCMinutes(),
        second: d.getUTCSeconds(),
    };
};

/** The wall-clock time in `timeZone` at `at`. */
export const wallClockIn = (timeZone: string, at: Date = new Date()): V1ClockTime => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hourCycle: 'h23',
    }).formatToParts(at);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);

    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        // Some engines still print midnight as 24 under h23.
        hour: get('hour') % 24,
        minute: get('minute'),
        second: get('second'),
    };
};

/** "Wed, Nov 4, 2026, 10:36" and "10:36", from a bare wall-clock time. */
export const formatClock = (t: V1ClockTime): { full: string; short: string } => {
    const d = new Date(clockToMs(t));
    const opts: Intl.DateTimeFormatOptions = { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
    return {
        full: d.toLocaleString('en-US', { ...opts, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
        short: d.toLocaleTimeString('en-US', opts),
    };
};
